import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { FreeForLifeBanner } from "@/components/welp/FreeBanner";
import { trackFb } from "@/lib/fbq";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { showBanner } from "@/lib/banner";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { Check, X, Eye, EyeOff } from "lucide-react";

const pwRules = (pw) => ([
  { ok: pw.length >= 8, t: "At least 8 characters" },
  { ok: /[A-Z]/.test(pw), t: "An uppercase letter" },
  { ok: /[a-z]/.test(pw), t: "A lowercase letter" },
  { ok: /\d/.test(pw), t: "A number" },
  { ok: /[^A-Za-z0-9]/.test(pw), t: "A special character" },
]);

function PasswordRules({ pw }) {
  return (
    <ul className="text-lg grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
      {pwRules(pw).map(r => (
        <li key={r.t} className={`flex items-center gap-1.5 ${r.ok ? "text-[#FF1A82]" : "text-[#5A5A5A]"}`}>
          {r.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {r.t}
        </li>
      ))}
    </ul>
  );
}

function PasswordField({ value, onChange, testid, placeholder = "Password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} type={show ? "text" : "password"} value={value} onChange={onChange} required placeholder={placeholder}
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 pr-12 text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
      <button type="button" onClick={() => setShow(s => !s)} data-testid={`${testid}-toggle`}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#FF1A82] transition" aria-label={show ? "Hide password" : "Show password"}>
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
}

function CaptchaBlock({ captcha, onRefresh, answer, setAnswer }) {
  return (
    <div className="rounded-2xl bg-white/70 backdrop-blur-lg shadow-lg border-2 border-[#FF1A82]/60 p-5 space-y-3 shadow-[0_0_24px_rgba(204,255,0,0.25)]">
      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#FF1A82]">Prove you're human</div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[#1A1A1A] text-xl font-semibold" data-testid="captcha-question">
          {captcha?.question || (
            <span className="inline-flex items-center gap-2 text-[#3A3A3A] text-base font-normal">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FF1A82] animate-pulse" />
              Loading…
            </span>
          )}
        </div>
        <button type="button" onClick={onRefresh} className="text-xs text-[#FF1A82] hover:text-[#10B981] underline font-medium" data-testid="captcha-refresh">refresh</button>
      </div>
      <input data-testid="captcha-answer" value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Your answer"
        className="w-full bg-gray-100 border-2 border-black/20 rounded-xl px-4 py-3 text-[#1A1A1A] text-lg font-medium outline-none focus:border-[#FF1A82] placeholder:text-[#5A5A5A]" />
    </div>
  );
}

function GoogleButton() {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={onClick} data-testid="google-login"
      className="w-full px-5 py-3 rounded-full border border-black/20 text-[#1A1A1A] bg-black/5 hover:bg-black/5 transition inline-flex items-center justify-center gap-2">
      <span className="font-medium">Continue with Google</span>
    </button>
  );
}

function WelpTagline() {
  return (
    <div className="text-xs uppercase tracking-[0.32em] mb-3">
      <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span>
    </div>
  );
}

export function PhoneVerifyCard({ hasPhone = false, phoneHint = "", onDone }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState(hasPhone ? "code" : "phone");
  const [hint, setHint] = useState(phoneHint || "");
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    try {
      const r = await api.phoneStart(phone);
      setHint(r.phone_hint); setStage("code");
      toast.success("Verification code sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the code — check the number."); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.phoneVerify(code);
      onDone(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed."); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    try { await api.phoneResend(); toast.success("New code sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't resend — try again in a bit."); }
  };

  return (
    <div className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border-2 border-[#10B981]/40 p-7" data-testid="phone-verify-card">
      <h2 className="font-heading text-2xl text-[#1A1A1A] font-bold">Verify your phone number</h2>
      <p className="text-lg text-[#3A3A3A] font-semibold">Every Welp account must have a verified phone number. Your number is never shown publicly.</p>
      {stage === "phone" ? (
        <>
          <input data-testid="verify-phone-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Your mobile number, e.g. 555-123-4567"
            className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-lg text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
          <p className="text-lg text-[#5A5A5A] font-semibold" data-testid="sms-consent-notice">
            By continuing, you agree to receive a one-time verification text message from Welp. Message and data rates may apply. We do not send marketing text messages.
          </p>
          <button type="button" onClick={sendCode} disabled={busy || phone.replace(/\D/g, "").length < 10} data-testid="verify-send-code-btn"
            className="w-full px-5 py-3 rounded-full bg-[#FF1A82] text-white text-lg font-semibold hover:brightness-110 transition disabled:opacity-50">
            {busy ? "Sending…" : "Text me a code"}
          </button>
        </>
      ) : (
        <>
          <p className="text-lg text-[#1A1A1A] font-semibold">We texted a 6-digit code to the number ending in <strong>{hint || "····"}</strong>.</p>
          <input data-testid="verify-code-input" inputMode="numeric" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code"
            className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-2xl tracking-[0.4em] text-center text-[#1A1A1A] font-bold outline-none focus:border-[#10B981]" />
          <button type="button" onClick={verify} disabled={busy || code.length !== 6} data-testid="verify-code-btn"
            className="w-full px-5 py-3 rounded-full bg-[#10B981] text-white text-lg font-semibold hover:brightness-110 transition disabled:opacity-50">
            {busy ? "Verifying…" : "Verify & activate my account"}
          </button>
          <div className="flex justify-between items-center">
            <button type="button" onClick={resend} className="text-lg text-[#FF1A82] hover:text-[#10B981] underline font-medium" data-testid="verify-resend-btn">Resend code</button>
            <button type="button" onClick={() => setStage("phone")} className="text-lg text-[#5A5A5A] hover:text-[#10B981] underline font-medium" data-testid="verify-change-phone-btn">Change number</button>
          </div>
        </>
      )}
    </div>
  );
}

function useVerifyDone(nextPath = "/account", message = "Phone verified!") {
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  return (r) => {
    if (r?.token) loginWithToken(r.token, r.user);
    showBanner(message);
    const waiting = r?.waiting_reviews || [];
    if (waiting.length > 0) {
      toast.success(`You have ${waiting.length} review${waiting.length > 1 ? "s" : ""} waiting for you!`);
      nav(`/respond/${waiting[0].review_id}`);
    } else {
      nav(nextPath);
    }
  };
}

export function VerifyPhonePage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const onDone = useVerifyDone("/account", "You found us. Welcome to your sanctuary.");
  useEffect(() => {
    if (loading) return;
    if (!user && !localStorage.getItem("welp_token")) { nav("/login"); return; }
    if (user?.phone_verified) nav("/account");
  }, [user, loading, nav]);
  return (
    <PageBg>
      <Nav />
      <section className="max-w-md mx-auto px-6 py-16" data-testid="verify-phone-page">
        <div className="text-veil">
          <WelpTagline />
          <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">One last <span className="italic text-[#10B981]">step.</span></h1>
        </div>
        <PhoneVerifyCard hasPhone={false} onDone={onDone} />
      </section>
    </PageBg>
  );
}

export function LoginPage() {
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/account";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(null);
  const onVerified = useVerifyDone(next, "It's about time. Get over here.");

  const refresh = () => api.captcha().then(setCaptcha).catch(() => toast.error("Could not load captcha. Try refresh."));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.login({ email, password, captcha_id: captcha.captcha_id, captcha_answer: answer });
      if (r.needs_phone_verification) {
        localStorage.setItem("welp_token", r.pending_token);
        setVerify({ has_phone: r.has_phone, phone_hint: r.phone_hint });
        toast.info("Please verify your phone number to finish signing in.");
        return;
      }
      loginWithToken(r.token, r.user);
      showBanner("It's about time. Get over here.");
      nav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      refresh(); setAnswer("");
    } finally { setBusy(false); }
  };

  if (verify) {
    return (
      <PageBg>
        <Nav />
        <section className="max-w-md mx-auto px-6 py-16">
          <div className="text-veil">
            <WelpTagline />
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Almost <span className="italic text-[#FF1A82]">there.</span></h1>
          </div>
          <PhoneVerifyCard hasPhone={verify.has_phone} phoneHint={verify.phone_hint} onDone={onVerified} />
        </section>
      </PageBg>
    );
  }

  return (
    <PageBg>
      <Nav />
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="text-veil">
        <WelpTagline />
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Welcome <span className="italic text-[#FF1A82]">back.</span></h1>
        </div>
        <FreeForLifeBanner />
        <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
          <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Email"
            className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
          <PasswordField testid="login-password" value={password} onChange={e => setPassword(e.target.value)} />
          <CaptchaBlock captcha={captcha} onRefresh={refresh} answer={answer} setAnswer={setAnswer} />
          <button disabled={busy} data-testid="login-submit" className="w-full px-5 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-sm text-[#FF1A82] hover:text-[#10B981] hover:underline font-medium" data-testid="forgot-password-link">Forgot your password?</Link>
          </div>
          <div className="text-center text-xs uppercase tracking-[0.22em] text-[#5A5A5A]">or</div>
          <GoogleButton />
          <p className="text-sm text-[#3A3A3A] text-center pt-2">New here? <Link to="/register" className="text-[#10B981] hover:underline">Create account</Link></p>
        </form>
      </section>
    </PageBg>
  );
}

export function RegisterPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(null);
  const onVerified = useVerifyDone("/account", "You found us. Welcome to your sanctuary.");

  const refresh = () => api.captcha().then(setCaptcha).catch(() => toast.error("Could not load captcha. Try refresh."));
  useEffect(() => { refresh(); }, []);

  const allOK = pwRules(password).every(r => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOK) {
      const missing = pwRules(password).filter(r => !r.ok).map(r => r.t);
      return toast.error(missing.length ? `Password needs: ${missing.join(", ")}` : "Password does not meet all requirements");
    }
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid mobile phone number.");
    setBusy(true);
    try {
      const r = await api.register({ email, password, display_name: name, phone, captcha_id: captcha.captcha_id, captcha_answer: answer });
      trackFb("CompleteRegistration");
      localStorage.setItem("welp_token", r.pending_token);
      setVerify({ phone_hint: r.phone_hint });
      toast.success("Almost done — enter the code we just texted you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
      refresh(); setAnswer("");
    } finally { setBusy(false); }
  };

  if (verify) {
    return (
      <PageBg>
        <Nav />
        <section className="max-w-md mx-auto px-6 py-16">
          <div className="text-veil">
            <WelpTagline />
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Check your <span className="italic text-[#10B981]">texts.</span></h1>
          </div>
          <PhoneVerifyCard hasPhone phoneHint={verify.phone_hint} onDone={onVerified} />
        </section>
      </PageBg>
    );
  }

  return (
    <PageBg>
      <Nav />
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="text-veil">
        <WelpTagline />
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Date with <span className="italic text-[#FF1A82]">integrity.</span></h1>
        </div>
        <FreeForLifeBanner />
        <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
          <input data-testid="reg-name" value={name} onChange={e => setName(e.target.value)} required placeholder="Choose a unique Welp username"
            className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
          <input data-testid="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Email"
            className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
          <div>
            <input data-testid="reg-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} required placeholder="Mobile number (required — we'll text a code)"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
            <p className="text-sm text-[#5A5A5A] font-semibold mt-1">Every account must verify a phone number. It's never shown publicly.</p>
          </div>
          <div>
            <PasswordField testid="reg-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a strong password" />
            <PasswordRules pw={password} />
          </div>
          <CaptchaBlock captcha={captcha} onRefresh={refresh} answer={answer} setAnswer={setAnswer} />
          <button disabled={busy} data-testid="reg-submit" className="w-full px-5 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition disabled:opacity-50 disabled:shadow-none">
            {busy ? "Creating…" : "Create account"}
          </button>
          <div className="text-center text-xs uppercase tracking-[0.22em] text-[#5A5A5A]">or</div>
          <GoogleButton />
          <p className="text-sm text-[#3A3A3A] text-center pt-2">Already a member? <Link to="/login" className="text-[#10B981] hover:underline">Sign in</Link></p>
        </form>
      </section>
    </PageBg>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const refresh = () => api.captcha().then(setCaptcha).catch(() => toast.error("Could not load captcha. Try refresh."));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.forgotPassword({ email, captcha_id: captcha.captcha_id, captcha_answer: answer });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset email");
      refresh(); setAnswer("");
    } finally { setBusy(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-md mx-auto px-6 py-16" data-testid="forgot-password-page">
        <div className="text-veil">
        <WelpTagline />
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Reset your <span className="italic text-[#10B981]">password.</span></h1>
        </div>
        {sent ? (
          <SmsCodeResetForm email={email} />
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <p className="text-lg text-[#3A3A3A]">Enter the email on your Welp account and we'll text a reset code to the verified phone on file.</p>
            <input data-testid="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Email"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82]" />
            <CaptchaBlock captcha={captcha} onRefresh={refresh} answer={answer} setAnswer={setAnswer} />
            <button disabled={busy} data-testid="forgot-submit" className="w-full px-5 py-3 rounded-full bg-[#10B981] text-white font-semibold hover:brightness-110 transition disabled:opacity-60">
              {busy ? "Sending…" : "Text me a reset code"}
            </button>
            <p className="text-sm text-[#3A3A3A] text-center pt-2">Remembered it? <Link to="/login" className="text-[#FF1A82] hover:underline">Sign in</Link></p>
          </form>
        )}
      </section>
    </PageBg>
  );
}

export function SmsCodeResetForm({ email }) {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const allOK = pwRules(password).every(r => r.ok);
  const matches = password.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOK) return toast.error("Your new password doesn't meet all the requirements yet.");
    if (!matches) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await api.resetPassword({ email, code: code.trim(), new_password: password });
      toast.success("Password updated! Sign in with your new password.");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset your password.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7" data-testid="sms-reset-form">
      <p className="text-lg text-[#1A1A1A] font-semibold">If an account exists for <span className="font-bold">{email}</span>, we just texted a 6-digit reset code to the phone on file. It expires in 15 minutes.</p>
      <input data-testid="reset-code" value={code} onChange={e => setCode(e.target.value)} required inputMode="numeric" maxLength={6} placeholder="6-digit code from the text"
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-lg text-[#1A1A1A] outline-none focus:border-[#10B981] tracking-[0.3em] text-center font-bold" />
      <input data-testid="reset-new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="New password"
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-lg text-[#1A1A1A] outline-none focus:border-[#10B981]" />
      <PasswordRules pw={password} />
      <input data-testid="reset-confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required placeholder="Confirm new password"
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-lg text-[#1A1A1A] outline-none focus:border-[#10B981]" />
      {confirm.length > 0 && !matches && <p className="text-lg text-[#FF1A82] font-semibold">Passwords don't match yet.</p>}
      <button disabled={busy} data-testid="reset-submit" className="w-full px-5 py-3 rounded-full bg-[#10B981] text-white text-lg font-semibold hover:brightness-110 transition disabled:opacity-60">
        {busy ? "Updating…" : "Set new password"}
      </button>
      <p className="text-lg text-[#3A3A3A] text-center pt-2">Didn't get the text? <Link to="/contact" className="text-[#FF1A82] hover:underline">Contact us</Link></p>
    </form>
  );
}

export function ResetPasswordPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const allOK = pwRules(password).every(r => r.ok);
  const matches = password.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOK) return toast.error("Password does not meet all requirements");
    if (!matches) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.resetPassword({ token, new_password: password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
        <div className="text-veil">
        <WelpTagline />
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Set a new <span className="italic text-[#FF1A82]">password.</span></h1>
        </div>
        {!token ? (
          <div className="mt-8 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <p className="text-[#1A1A1A]">This reset link is missing its token. Please request a fresh link.</p>
            <Link to="/forgot-password" className="mt-4 inline-block text-[#10B981] hover:underline">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <div>
              <PasswordField testid="reset-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="New password" />
              <PasswordRules pw={password} />
            </div>
            <PasswordField testid="reset-confirm" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm new password" />
            {confirm.length > 0 && !matches && <p className="text-xs text-[#10B981]">Passwords do not match.</p>}
            <button disabled={busy || !allOK || !matches} data-testid="reset-submit" className="w-full px-5 py-3 rounded-full bg-[#FF1A82] text-white font-semibold hover:brightness-110 transition disabled:opacity-50">
              {busy ? "Updating…" : "Update password"}
            </button>
            <p className="text-sm text-[#3A3A3A] text-center pt-2"><Link to="/login" className="text-[#10B981] hover:underline">Back to sign in</Link></p>
          </form>
        )}
      </section>
    </PageBg>
  );
}
